import { useState } from "react"
import { NavLink, useLocation } from "react-router-dom"
import { 
  ChevronDown, 
  ChevronRight, 
  LayoutDashboard, 
  DollarSign, 
  BarChart3, 
  Users,
  Settings,
  Search,
  Moon,
  Sun,
  Menu,
  X
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface NavItem {
  name: string
  path: string
  icon: React.ReactNode
  children?: NavItem[]
}

const navigation: NavItem[] = [
  { 
    name: "Home", 
    path: "/", 
    icon: <LayoutDashboard className="h-4 w-4" /> 
  },
  { 
    name: "AdMob Revenue", 
    path: "/admob", 
    icon: <DollarSign className="h-4 w-4" /> 
  },
  { 
    name: "App Analytics", 
    path: "/analytics", 
    icon: <BarChart3 className="h-4 w-4" /> 
  },
  { 
    name: "Retention", 
    path: "/retention", 
    icon: <Users className="h-4 w-4" /> 
  },
  { 
    name: "Settings", 
    path: "/settings", 
    icon: <Settings className="h-4 w-4" /> 
  },
]

interface SidebarProps {
  isCollapsed: boolean
  onToggle: () => void
  onOpenSearch: () => void
  theme: "light" | "dark"
  onToggleTheme: () => void
}

export function Sidebar({ 
  isCollapsed, 
  onToggle, 
  onOpenSearch, 
  theme, 
  onToggleTheme 
}: SidebarProps) {
  const location = useLocation()
  const [expandedSections, setExpandedSections] = useState<string[]>(["pages"])

  const toggleSection = (section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    )
  }

  return (
    <>
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col bg-background border-r transition-all duration-300",
          isCollapsed ? "w-16" : "w-64"
        )}
      >
        <div className="flex items-center justify-between h-14 px-4 border-b">
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                X
              </div>
              <span className="font-semibold text-sm">Xantus Reports</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="h-8 w-8"
          >
            {isCollapsed ? <Menu className="h-4 w-4" /> : <X className="h-4 w-4" />}
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto py-4">
          {!isCollapsed && (
            <div className="px-3 mb-4">
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
                onClick={onOpenSearch}
              >
                <Search className="h-4 w-4" />
                <span className="text-sm">Quick Search</span>
                <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                  <span className="text-xs">⌘K</span>
                </kbd>
              </Button>
            </div>
          )}

          {isCollapsed && (
            <div className="px-3 mb-4">
              <Button
                variant="ghost"
                size="icon"
                className="w-full"
                onClick={onOpenSearch}
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
          )}

          <Separator className="mb-4" />

          <div className="px-3">
            {!isCollapsed && (
              <button
                onClick={() => toggleSection("pages")}
                className="flex items-center gap-1 w-full px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {expandedSections.includes("pages") ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                <span>PAGES</span>
              </button>
            )}

            <nav className="space-y-1 mt-2">
              {navigation.map((item) => {
                const isActive = location.pathname === item.path
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-3 px-2 py-1.5 rounded-md text-sm font-medium transition-colors",
                      isActive
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
                      isCollapsed && "justify-center px-0"
                    )}
                    title={isCollapsed ? item.name : undefined}
                  >
                    {item.icon}
                    {!isCollapsed && <span>{item.name}</span>}
                  </NavLink>
                )
              })}
            </nav>
          </div>
        </div>

        <div className="border-t p-3">
          <div className={cn("flex items-center gap-2", isCollapsed && "justify-center")}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  {theme === "light" ? (
                    <Sun className="h-4 w-4" />
                  ) : (
                    <Moon className="h-4 w-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onToggleTheme}>
                  {theme === "light" ? "Dark mode" : "Light mode"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {isCollapsed && (
        <div className="fixed inset-y-0 left-16 w-64 bg-background border-r z-40 transform -translate-x-full" />
      )}
    </>
  )
}
